/**
 * Sell-side RFQ / RFT commercial particulars. Supplier quotations stay in Procurement.
 */
import React from 'react';
import { Button, Form, Input, InputNumber, Select, Switch, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CLIENT_INVITATION_NOTE,
  RFT_STATUS_OPTIONS,
  TENDER_INVITATION_STATUSES,
  epcInvitationKind,
} from '@/constants/epcInvitationKind';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { Alert } from 'antd';

export const EpcTenderInvitationForm: React.FC<{ tender: Record<string, unknown> }> = ({
  tender,
}) => {
  const qc = useQueryClient();
  const id = String(tender.id || '');
  const kind = epcInvitationKind(tender.invitation_kind || tender.invitationKind) || 'RFT';
  const [form] = Form.useForm();

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => epcCommercialService.patchTender(id, values),
    onSuccess: () => {
      message.success('Invitation saved');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', id] });
      qc.invalidateQueries({ queryKey: ['epc-tenders'] });
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  return (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message={CLIENT_INVITATION_NOTE} />
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: tender.status || 'Draft',
          clientDocumentNo: tender.client_document_no,
          timezone: tender.timezone,
          submissionChannel: tender.submission_channel,
          confidentiality: tender.confidentiality,
          scopeSummary: tender.scope_summary,
          siteLocation: tender.site_location,
          clientContactName: tender.client_contact_name,
          quotationValidityDays: tender.quotation_validity_days,
          exclusionsAssumptions: tender.exclusions_assumptions,
          deliveryBasis: tender.delivery_basis,
          technicalResponseRequired: Boolean(tender.technical_response_required),
          envelopeStructure: tender.envelope_structure,
          tenderSecurityRequired: Boolean(tender.tender_security_required),
          tenderSecurityAmount: tender.tender_security_amount,
          tenderSecurityCurrency: tender.tender_security_currency,
          tenderSecurityExpiry: tender.tender_security_expiry,
          technicalWeight: tender.technical_weight,
          commercialWeight: tender.commercial_weight,
          bafoAllowed: Boolean(tender.bafo_allowed),
          alternativeBidAllowed: Boolean(tender.alternative_bid_allowed),
          mandatorySiteVisit: Boolean(tender.mandatory_site_visit),
        }}
        onFinish={(values) => save.mutate(values)}
      >
        <Form.Item name="status" label="Status">
          <Select
            options={(kind === 'RFT' ? RFT_STATUS_OPTIONS : TENDER_INVITATION_STATUSES).map(
              (value) => ({ value, label: value })
            )}
          />
        </Form.Item>
        <Form.Item name="clientDocumentNo" label="Client document number">
          <Input />
        </Form.Item>
        <Form.Item name="timezone" label="Closing timezone">
          <Input placeholder="Asia/Dubai" />
        </Form.Item>
        <Form.Item name="submissionChannel" label="Submission channel">
          <Select
            allowClear
            options={['Portal', 'Email', 'Hard copy', 'Mixed'].map((value) => ({
              value,
              label: value,
            }))}
          />
        </Form.Item>
        <Form.Item name="confidentiality" label="Confidentiality">
          <Select
            allowClear
            options={['Public', 'Confidential', 'Restricted'].map((value) => ({
              value,
              label: value,
            }))}
          />
        </Form.Item>
        <Form.Item name="siteLocation" label="Site / location">
          <Input />
        </Form.Item>
        <Form.Item name="clientContactName" label="Client contact">
          <Input />
        </Form.Item>
        <Form.Item name="scopeSummary" label="Scope summary">
          <Input.TextArea rows={2} />
        </Form.Item>
        {kind === 'RFQ' ? (
          <>
            <Form.Item name="quotationValidityDays" label="Quotation validity (days)">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="deliveryBasis" label="Delivery basis">
              <Input />
            </Form.Item>
            <Form.Item name="exclusionsAssumptions" label="Exclusions and assumptions">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item
              name="technicalResponseRequired"
              label="Technical response required"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item name="envelopeStructure" label="Envelope structure">
              <Select
                allowClear
                options={[
                  { value: 'Single', label: 'Single' },
                  { value: 'TwoEnvelope', label: 'Two envelope' },
                  { value: 'TwoStage', label: 'Two stage' },
                ]}
              />
            </Form.Item>
            <Form.Item name="technicalWeight" label="Technical evaluation weight">
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="commercialWeight" label="Commercial evaluation weight">
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="tenderSecurityRequired"
              label="Tender security required"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item name="tenderSecurityAmount" label="Tender security amount">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="tenderSecurityCurrency" label="Tender security currency">
              <Input />
            </Form.Item>
            <Form.Item name="tenderSecurityExpiry" label="Tender security expiry">
              <Input placeholder="YYYY-MM-DD" />
            </Form.Item>
            <Form.Item name="bafoAllowed" label="BAFO allowed" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              name="alternativeBidAllowed"
              label="Alternative bid allowed"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="mandatorySiteVisit"
              label="Mandatory site visit"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </>
        )}
        <Button type="primary" htmlType="submit" loading={save.isPending}>
          Save invitation
        </Button>
      </Form>
      {tender.predecessor_id ? (
        <p style={{ marginTop: 8 }}>
          Predecessor {String(tender.predecessor_entity || '')} {String(tender.predecessor_id)}
        </p>
      ) : null}
    </>
  );
};
